import React from "react";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import CompetitionStandingsTable from "./CompetitionStandingsTable";
import ZoneLegend, { promotionsFromRows, dedupePromotions } from "./shared/ZoneLegend";
import { useCompetitionsTr } from "./shared/useCompetitionsTr";
import { groupLetter } from "./shared/competitionsFormat";

// CompetitionGroups — the "Groups → KO" group phase (UI-SPEC §14, K2b):
// one compact table per group, 2-column grid at ≥768px, stacked single column
// on mobile (declared: each group's table switches to the K9 card pattern via
// CompetitionStandingsTable's own mobile branch — nothing shrinks). One shared
// zone legend under the grid ("Advances to knockout phase" when the provider's
// promotions carry no own naming is the PAGE's choice — the component renders
// exactly the promotions passed / found on the rows).
//
// Self-contained: props in, markup out. Row action slot and onNavigate are
// forwarded to every group table. Empty groups are absent; no groups -> null.
const CompetitionGroups = ({
  groups,
  promotions,
  onNavigate,
  navigable,
  renderRowAction,
}) => {
  const { trAny } = useCompetitionsTr();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const visible = (Array.isArray(groups) ? groups : []).filter(
    (g) => g && Array.isArray(g.rows) && g.rows.length > 0,
  );
  if (visible.length === 0) return null; // empty renders nothing

  const legendItems =
    promotions && promotions.length
      ? dedupePromotions(promotions)
      : dedupePromotions(visible.flatMap((g) => promotionsFromRows(g.rows)));

  const nameOf = (group, index) => {
    if (group.name) return trAny(group.name);
    const letter = group.letter || groupLetter(group.number) || groupLetter(index + 1);
    return letter ? trAny(`Group ${letter}`) : null;
  };

  return (
    <div className="tp-comp-groups" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "repeat(2, minmax(0,1fr))",
          gap: 20,
          alignItems: "start",
        }}
      >
        {visible.map((group, index) => {
          const title = nameOf(group, index);
          return (
            <div
              key={group.id || group.name || group.letter || index}
              data-testid="group-card"
              style={{ display: "flex", flexDirection: "column" }}
            >
              {title && (
                <div
                  style={{
                    padding: "12px 18px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0F172A",
                    border: "1px solid #E2E8F0",
                    borderBottom: "none",
                    borderRadius: "3px 3px 0 0",
                    background: "#fff",
                  }}
                >
                  {title}
                </div>
              )}
              <CompetitionStandingsTable
                rows={group.rows}
                variant="compact"
                showLegend={false}
                squareTop={Boolean(title)}
                onNavigate={onNavigate}
                navigable={navigable}
                renderRowAction={renderRowAction}
              />
            </div>
          );
        })}
      </div>
      <ZoneLegend promotions={legendItems} />
    </div>
  );
};

export default CompetitionGroups;
